import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Pagination from "@/components/Pagination";
import { toast } from "sonner";
import {
  Swap, ArrowRight, Plus, Camera, Trash, ArrowDown, ArrowUp,
  CheckCircle, XCircle, Clock, ShieldCheck,
} from "@phosphor-icons/react";

const fmtQty = (n) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(n || 0);

const APPROVAL_PILL = {
  pending_approval: { label: "Pending Approval", bg: "bg-[#FEF7E0]", text: "text-[#B06000]", border: "border-[#FCE39E]" },
  approved: { label: "Approved", bg: "bg-[#E6F4EA]", text: "text-[#137333]", border: "border-[#A4D0B3]" },
  rejected: { label: "Rejected", bg: "bg-[#FCE8E6]", text: "text-[#C5221F]", border: "border-[#F3B2AC]" },
};

export default function Transfers() {
  const { activeProject, activeProjectId, projects } = useProject();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [direction, setDirection] = useState("all");
  const [approvalFilter, setApprovalFilter] = useState("all");

  const load = React.useCallback(async (targetPage = 1) => {
    if (!activeProjectId) return;
    setLoading(true);
    try {
      const params = { page: targetPage, page_size: pageSize };
      if (direction !== "all") params.direction = direction;
      if (approvalFilter !== "all") params.approval_status = approvalFilter;
      const { data } = await api.get("/transfers", { params });
      setRows(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
      setPage(data.page || 1);
    } finally {
      setLoading(false);
    }
  }, [direction, approvalFilter, activeProjectId, pageSize]);

  useEffect(() => { load(1); }, [load]);

  const handleApprove = async (id) => {
    if (!window.confirm("Approve this transfer? Source stock will be deducted and destination credited.")) return;
    try {
      await api.post(`/transfers/${id}/approve`);
      toast.success("Transfer approved — stock balances updated");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const handleReject = async (id) => {
    const notes = window.prompt("Reason for rejecting this transfer? (required)");
    if (notes === null) return;
    if (!notes.trim()) {
      toast.error("Notes are required to reject a transfer.");
      return;
    }
    try {
      const fd = new FormData();
      fd.append("notes", notes.trim());
      await api.post(`/transfers/${id}/reject`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Transfer rejected");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const handleReverse = async (id) => {
    if (!window.confirm("Reverse this approved transfer? Stock balances will rebound for both projects.")) return;
    try {
      await api.delete(`/transfers/${id}`);
      toast.success("Transfer reversed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const pendingCount = rows.filter((r) => r.approval_status === "pending_approval").length;

  return (
    <div className="p-8 sm:p-10 max-w-[1600px] mx-auto" data-testid="transfers-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-neutral-500 mb-2 flex items-center gap-2">
            {activeProject && (
              <>
                <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: activeProject.color || "#0047FF" }} />
                <span>{activeProject.name}</span>
                <span className="text-neutral-300">·</span>
              </>
            )}
            <span>Logistics</span>
          </div>
          <h1 className="font-display text-4xl font-bold tracking-tight flex items-center gap-3">
            <Swap size={32} className="text-primary" weight="fill" />
            Site Transfers
          </h1>
          <p className="text-sm text-neutral-500 mt-2">
            {total} transfer{total === 1 ? "" : "s"} touching {activeProject?.name || "this project"}.
            Stock reflects only once an admin approves the delivery.
            {pendingCount > 0 && (
              <span className="ml-2 inline-flex items-center gap-1 text-[#B06000]" data-testid="pending-count-banner">
                <Clock size={13} weight="fill" />
                {pendingCount} pending admin approval
              </span>
            )}
          </p>
        </div>
        <Link to="/transfers/new">
          <Button className="rounded-sm h-11 px-5 bg-primary hover:bg-[#0039CC] text-white font-medium gap-2" data-testid="new-transfer-btn">
            <Plus size={16} weight="bold" /> New Transfer
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white border border-neutral-200 rounded-sm p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div className="w-[200px]">
          <label className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1 block">Direction</label>
          <Select value={direction} onValueChange={setDirection}>
            <SelectTrigger className="rounded-sm h-10" data-testid="direction-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Transfers</SelectItem>
              <SelectItem value="in">Inbound (received here)</SelectItem>
              <SelectItem value="out">Outbound (sent from here)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-[200px]">
          <label className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1 block">Approval Status</label>
          <Select value={approvalFilter} onValueChange={setApprovalFilter}>
            <SelectTrigger className="rounded-sm h-10" data-testid="approval-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="pending_approval">Pending Approval</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="text-xs text-neutral-500 ml-auto">
          {projects.length} project{projects.length === 1 ? "" : "s"} configured · {isAdmin ? "Admin can approve/reject" : "Initiators wait for admin approval"}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-neutral-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-600">
              <tr>
                <th className="text-left px-6 py-3 font-medium text-xs uppercase tracking-wider">Date</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Flow</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Category</th>
                <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wider">Quantity</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Status</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Photos</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">By</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-12 text-neutral-400">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-16 text-neutral-400">
                  No transfers involving {activeProject?.name} yet. Click "New Transfer" to move material between sites.
                </td></tr>
              ) : (
                rows.map((r, idx) => {
                  const isInbound = r.to_project_id === activeProjectId;
                  const approval = APPROVAL_PILL[r.approval_status] || APPROVAL_PILL.pending_approval;
                  const isPending = r.approval_status === "pending_approval";
                  const isApproved = r.approval_status === "approved";
                  // Pending transfers don't affect stock — visually subdue them
                  const qtyMuted = isPending || r.approval_status === "rejected";
                  return (
                    <tr key={r.id} className={`border-t border-neutral-100 hover:bg-neutral-50 ${idx % 2 ? "bg-neutral-50/40" : ""}`} data-testid={`transfer-row-${r.id}`}>
                      <td className="px-6 py-3 text-neutral-500 tabular-nums whitespace-nowrap">
                        {new Date(r.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ${
                            isInbound ? "bg-[#E6F4EA] text-[#137333] border-[#A4D0B3]" : "bg-[#FEF7E0] text-[#B06000] border-[#FCE39E]"
                          }`}>
                            {isInbound ? <ArrowDown size={11} weight="bold" /> : <ArrowUp size={11} weight="bold" />}
                            {isInbound ? "IN" : "OUT"}
                          </span>
                          <span className="text-xs text-neutral-700 flex items-center gap-1.5 truncate">
                            <span className="truncate">{r.from_project_name}</span>
                            <ArrowRight size={12} className="text-neutral-400" />
                            <span className="truncate">{r.to_project_name}</span>
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-neutral-700">{r.category}</td>
                      <td className={`px-4 py-3 text-right tabular-nums font-medium ${qtyMuted ? "text-neutral-400" : isInbound ? "text-[#137333]" : "text-[#B06000]"}`}>
                        {isApproved && (isInbound ? "+" : "−")}{fmtQty(r.quantity)} <span className="text-neutral-500 text-xs">{r.unit}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ${approval.bg} ${approval.text} ${approval.border}`}
                          data-testid={`approval-pill-${r.id}`}
                          title={r.approval_status === "rejected" && r.approval_notes ? `Reason: ${r.approval_notes}` : undefined}
                        >
                          {approval.label}
                        </span>
                        {r.approved_by_name && r.approval_status !== "pending_approval" && (
                          <div className="text-[10px] text-neutral-500 mt-0.5">
                            by {r.approved_by_name}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {r.photos?.length > 0 ? (
                          <span className="inline-flex items-center gap-1 text-xs text-neutral-600">
                            <Camera size={14} /> {r.photos.length}
                          </span>
                        ) : (
                          <span className="text-neutral-300 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-neutral-600 text-xs">{r.created_by_name || "—"}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {isAdmin && isPending && (
                            <>
                              <button
                                onClick={() => handleApprove(r.id)}
                                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] uppercase tracking-wider text-[#137333] hover:bg-[#E6F4EA] border border-[#A4D0B3] rounded-sm"
                                title="Approve transfer (admin)"
                                data-testid={`approve-transfer-${r.id}`}
                              >
                                <CheckCircle size={12} weight="fill" /> Approve
                              </button>
                              <button
                                onClick={() => handleReject(r.id)}
                                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] uppercase tracking-wider text-[#C5221F] hover:bg-[#FCE8E6] border border-[#F3B2AC] rounded-sm"
                                title="Reject transfer (admin)"
                                data-testid={`reject-transfer-${r.id}`}
                              >
                                <XCircle size={12} weight="fill" /> Reject
                              </button>
                            </>
                          )}
                          {isAdmin && isApproved && (
                            <button
                              onClick={() => handleReverse(r.id)}
                              className="text-neutral-400 hover:text-destructive p-1"
                              title="Reverse approved transfer (admin)"
                              data-testid={`reverse-transfer-${r.id}`}
                            >
                              <Trash size={16} />
                            </button>
                          )}
                          {!isAdmin && isPending && (
                            <span className="inline-flex items-center gap-1 text-[10px] text-neutral-400" data-testid={`awaiting-admin-${r.id}`}>
                              <ShieldCheck size={12} /> awaiting admin
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          totalPages={totalPages}
          onPageChange={(n) => load(n)}
          testIdPrefix="transfers"
        />
      </div>
    </div>
  );
}
